// ========================================
// MEDIA MANAGER
// Gestionnaire de la bibliothèque de médias
// ========================================

import React, { useState, useRef } from 'react';
import AdminLayout from '../../../components/admin/Layout/AdminLayout';
import { uploadToCloudinary } from '../../../services/cloudinary/config';
import { useMediaLibrary } from '../../../hooks';
import {
  FiUploadCloud,
  FiImage,
  FiTrash2,
  FiSearch,
  FiX,
  FiLoader,
  FiCheck,
  FiCopy,
  FiGrid,
  FiList,
  FiDownload,
  FiEye,
  FiFile,
  FiRefreshCw
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import './MediaManager.css';

const MediaManager = () => {
  // ========== HOOK FIREBASE ==========
  const { 
    medias, 
    loading, 
    fetchMedias,
    uploadMedia,
    uploadMultipleMedias,
    removeMultipleMedias 
  } = useMediaLibrary();

  // ========== ÉTATS LOCAUX ==========
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [filterType, setFilterType] = useState('all');
  const [selectedMedias, setSelectedMedias] = useState([]);
  const [previewMedia, setPreviewMedia] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const fileInputRef = useRef(null);

  // ========== UPLOAD DE FICHIERS ==========
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    const uploadedMedias = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      try {
        // Déterminer le type
        let mediaType = 'document';
        let folder = 'portfolio/documents';
        
        if (file.type.startsWith('image/')) {
          mediaType = 'image';
          folder = 'portfolio/images';
        } else if (file.type.startsWith('video/')) {
          mediaType = 'video';
          folder = 'portfolio/videos';
        }

        const result = await uploadToCloudinary(file, folder, (progress) => {
          const totalProgress = ((i / files.length) * 100) + (progress / files.length);
          setUploadProgress(Math.round(totalProgress));
        });

        uploadedMedias.push({
          name: file.name,
          url: result.url,
          publicId: result.publicId,
          type: mediaType,
          mimeType: file.type,
          size: file.size,
          width: result.width || null,
          height: result.height || null,
          folder: folder
        });

      } catch (error) {
        console.error('Erreur upload:', error);
        toast.error(`Erreur lors de l'upload de ${file.name}`);
      }
    }

    // Enregistrer dans Firebase
    if (uploadedMedias.length > 0) {
      if (uploadedMedias.length === 1) {
        await uploadMedia(uploadedMedias[0]);
      } else {
        await uploadMultipleMedias(uploadedMedias);
      }
      toast.success(`${uploadedMedias.length} fichier(s) uploadé(s)`);
    }

    setUploading(false);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // ========== SÉLECTION ==========
  const toggleSelect = (mediaId) => {
    setSelectedMedias(prev => {
      if (prev.includes(mediaId)) {
        return prev.filter(id => id !== mediaId);
      }
      return [...prev, mediaId];
    });
  };

  const selectAll = () => {
    if (selectedMedias.length === filteredMedias.length) {
      setSelectedMedias([]);
    } else {
      setSelectedMedias(filteredMedias.map(m => m.id));
    }
  };

  // ========== COPIER L'URL ==========
  const copyUrl = (url, e) => {
    if (e) e.stopPropagation();
    navigator.clipboard.writeText(url);
    toast.success('URL copiée !');
  };

  // ========== SUPPRIMER ==========
  const handleDeleteSelected = async () => {
    await removeMultipleMedias(selectedMedias);
    setSelectedMedias([]);
    setShowDeleteConfirm(false);
  };

  // ========== FILTRAGE ==========
  const filteredMedias = medias.filter(media => {
    const matchesSearch = media.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || media.type === filterType;
    return matchesSearch && matchesType;
  });

  // ========== HELPERS ==========
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return '';
    const date = dateValue?.toDate ? dateValue.toDate() : new Date(dateValue);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getMediaIcon = (type) => {
    switch (type) {
      case 'image': return <FiImage />;
      case 'video': return '🎬';
      case 'document': return <FiFile />;
      default: return <FiFile />;
    }
  };

  // ========== RENDU ==========
  return (
    <AdminLayout>
      <div className="media-manager">
        {/* Header */}
        <div className="manager-header">
          <div className="list-header-left">
            <h1>Bibliothèque de médias</h1>
            <span className="count-badge">{medias.length} fichiers</span>
          </div>

          <div className="header-actions">
            <button
              className="btn btn-ghost"
              onClick={fetchMedias}
              disabled={loading}
              title="Rafraîchir"
            >
              <FiRefreshCw className={loading ? 'spinner' : ''} />
            </button>
            
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept="image/*,.pdf,.doc,.docx"
              multiple
              style={{ display: 'none' }}
            />
            <button
              className="btn btn-primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <FiLoader className="spinner" />
                  {uploadProgress}%
                </>
              ) : (
                <>
                  <FiUploadCloud />
                  Uploader
                </>
              )}
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="media-toolbar">
          <div className="toolbar-left">
            <div className="search-box">
              <FiSearch />
              <input
                type="text"
                placeholder="Rechercher un fichier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="clear-search" onClick={() => setSearchTerm('')}>
                  <FiX />
                </button>
              )}
            </div>

            <div className="filter-tabs">
              <button
                className={`filter-tab ${filterType === 'all' ? 'active' : ''}`}
                onClick={() => setFilterType('all')}
              >
                Tous
              </button>
              <button
                className={`filter-tab ${filterType === 'image' ? 'active' : ''}`}
                onClick={() => setFilterType('image')}
              >
                <FiImage /> Images
              </button>
              <button
                className={`filter-tab ${filterType === 'document' ? 'active' : ''}`}
                onClick={() => setFilterType('document')}
              >
                <FiFile /> Documents
              </button>
            </div>
          </div>

          <div className="toolbar-right">
            {selectedMedias.length > 0 && (
              <div className="selection-actions">
                <span>{selectedMedias.length} sélectionné(s)</span>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setSelectedMedias([])}
                >
                  Désélectionner
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <FiTrash2 />
                  Supprimer
                </button>
              </div>
            )}

            <div className="view-toggle">
              <button
                className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
              >
                <FiGrid />
              </button>
              <button
                className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
              >
                <FiList />
              </button>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && medias.length === 0 && (
          <div className="loading-state">
            <FiLoader className="spinner" />
            <p>Chargement des médias...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && medias.length === 0 && (
          <div 
            className="upload-zone-large"
            onClick={() => fileInputRef.current?.click()}
          >
            <FiUploadCloud />
            <h3>Aucun média</h3>
            <p>Cliquez ou glissez vos fichiers ici pour les uploader</p>
            <span>Images (JPG, PNG, GIF, WebP) et documents (PDF, DOC)</span>
          </div>
        )}

        {/* No Results */}
        {!loading && medias.length > 0 && filteredMedias.length === 0 && (
          <div className="no-results">
            <FiSearch />
            <h3>Aucun résultat</h3>
            <p>Aucun média ne correspond à votre recherche</p>
            <button className="btn btn-ghost" onClick={() => {
              setSearchTerm('');
              setFilterType('all');
            }}>
              Réinitialiser les filtres
            </button>
          </div>
        )}

        {/* Media Grid/List */}
        {!loading && filteredMedias.length > 0 && (
          <>
            <div className="select-all-row">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={selectedMedias.length === filteredMedias.length && filteredMedias.length > 0}
                  onChange={selectAll}
                />
                <span>Tout sélectionner</span>
              </label>
              <span className="results-count">
                {filteredMedias.length} résultat(s)
              </span>
            </div>

            <div className={`media-${viewMode}`}>
              {filteredMedias.map(media => (
                <div 
                  key={media.id} 
                  className={`media-item ${selectedMedias.includes(media.id) ? 'selected' : ''}`}
                >
                  <div 
                    className="media-checkbox"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSelect(media.id);
                    }}
                  >
                    {selectedMedias.includes(media.id) ? (
                      <FiCheck />
                    ) : (
                      <div className="checkbox-empty"></div>
                    )}
                  </div>

                  <div 
                    className="media-preview"
                    onClick={() => setPreviewMedia(media)}
                  >
                    {media.type === 'image' ? (
                      <img src={media.url} alt={media.name} loading="lazy" />
                    ) : (
                      <div className="file-icon">
                        {getMediaIcon(media.type)}
                      </div>
                    )}
                  </div>

                  <div className="media-info">
                    <span className="media-name" title={media.name}>
                      {media.name}
                    </span>
                    {viewMode === 'list' && (
                      <>
                        <span className="media-type">{media.type}</span>
                        <span className="media-size">{formatFileSize(media.size)}</span>
                        {media.width && (
                          <span className="media-dimensions">{media.width}×{media.height}</span>
                        )}
                        <span className="media-date">{formatDate(media.uploadedAt)}</span>
                      </>
                    )}
                  </div>

                  <div className="media-actions">
                    <button
                      className="action-btn"
                      onClick={(e) => copyUrl(media.url, e)}
                      title="Copier l'URL"
                    >
                      <FiCopy />
                    </button>
                    <button
                      className="action-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviewMedia(media);
                      }}
                      title="Aperçu"
                    >
                      <FiEye />
                    </button>
                    <a
                      href={media.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="action-btn"
                      title="Télécharger"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FiDownload />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Preview Modal */}
        {previewMedia && (
          <div className="media-preview-modal" onClick={() => setPreviewMedia(null)}>
            <div className="preview-content" onClick={e => e.stopPropagation()}>
              <button className="close-preview" onClick={() => setPreviewMedia(null)}>
                <FiX />
              </button>
              
              {previewMedia.type === 'image' ? (
                <img src={previewMedia.url} alt={previewMedia.name} />
              ) : (
                <div className="file-preview">
                  <div className="file-icon-large">
                    {getMediaIcon(previewMedia.type)}
                  </div>
                  <p>{previewMedia.name}</p>
                </div>
              )}

              <div className="preview-info">
                <div className="preview-details">
                  <div className="detail-row">
                    <span className="detail-label">Nom:</span>
                    <span className="detail-value">{previewMedia.name}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Type:</span>
                    <span className="detail-value">{previewMedia.mimeType || previewMedia.type}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Taille:</span>
                    <span className="detail-value">{formatFileSize(previewMedia.size)}</span>
                  </div>
                  {previewMedia.width && (
                    <div className="detail-row">
                      <span className="detail-label">Dimensions:</span>
                      <span className="detail-value">{previewMedia.width}×{previewMedia.height}px</span>
                    </div>
                  )}
                  <div className="detail-row">
                    <span className="detail-label">Uploadé le:</span>
                    <span className="detail-value">{formatDate(previewMedia.uploadedAt)}</span>
                  </div>
                </div>
                
                <div className="preview-url">
                  <label>URL du fichier:</label>
                  <div className="url-box">
                    <input 
                      type="text" 
                      value={previewMedia.url} 
                      readOnly 
                      onClick={(e) => e.target.select()}
                    />
                    <button 
                      className="btn btn-primary btn-sm" 
                      onClick={() => copyUrl(previewMedia.url)}
                    >
                      <FiCopy />
                    </button>
                  </div>
                </div>

                <div className="preview-actions">
                  <a 
                    href={previewMedia.url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="btn btn-ghost"
                  >
                    <FiDownload /> Télécharger
                  </a>
                  <button 
                    className="btn btn-danger"
                    onClick={async () => {
                      await removeMultipleMedias([previewMedia.id]);
                      setPreviewMedia(null);
                    }}
                  >
                    <FiTrash2 /> Supprimer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-icon danger">
                <FiTrash2 />
              </div>
              <h3>Supprimer {selectedMedias.length} média(s) ?</h3>
              <p>Cette action est irréversible. Les fichiers seront supprimés de la bibliothèque.</p>
              <div className="modal-actions">
                <button
                  className="btn btn-ghost"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Annuler
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleDeleteSelected}
                >
                  <FiTrash2 />
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default MediaManager;